<template>
  <div class="product-dashboard">
    <!-- 顶部标题 -->
    <div class="header-section">
      <h2 class="page-title">团购券商品管理</h2>
      <p class="page-subtitle">管理直播间的商品团购券，支持增删改查</p>
    </div>

    <div class="container">
      <!-- 工具栏 -->
      <div class="tableBar">
        <div class="search-area">
          <el-input
            v-model="searchKeyword"
            placeholder="请输入商品名称"
            style="width: 250px"
            clearable
            @keyup.enter="handleSearch"
          >
            <i slot="prefix" class="el-input__icon el-icon-search" style="cursor: pointer" @click="handleSearch"></i>
          </el-input>
          <el-button type="primary" icon="el-icon-search" @click="handleSearch">搜索</el-button>
          <el-button icon="el-icon-refresh" @click="resetSearch">重置</el-button>
        </div>
        
        <div class="action-area">
          <el-button type="success" icon="el-icon-plus" @click="handleAdd">新增商品</el-button>
        </div>
      </div>

      <!-- 商品表格 -->
      <el-table
        :data="tableData"
        v-loading="loading"
        border
        stripe
        style="width: 100%"
        class="data-table"
      >
        <el-table-column type="index" label="序号" width="60" align="center"></el-table-column>
        
        <el-table-column label="商品图片" width="100" align="center">
          <template #default="scope">
            <el-image
              style="width: 60px; height: 60px; border-radius: 4px;"
              :src="scope.row.imageUrl"
              :preview-src-list="[scope.row.imageUrl]"
              fit="cover"
            >
              <div slot="error" class="image-error">
                <i class="el-icon-picture-outline"></i>
              </div>
            </el-image>
          </template>
        </el-table-column>
        
        <el-table-column prop="title" label="商品名称" min-width="250" show-overflow-tooltip></el-table-column>
        
        <el-table-column prop="shopName" label="店铺名称" width="200" show-overflow-tooltip></el-table-column>
        
        <el-table-column label="价格" width="150" align="center">
          <template #default="scope">
            <span class="price-text">¥{{ scope.row.price }}</span>
            <span v-if="scope.row.originalPrice" class="original-price">原价¥{{ scope.row.originalPrice }}</span>
          </template>
        </el-table-column>
        
        <el-table-column prop="soldCount" label="已售数量" width="100" align="center"></el-table-column>
        
        <el-table-column prop="stock" label="库存" width="80" align="center"></el-table-column>
        
        <el-table-column label="标签" width="200">
          <template #default="scope">
            <el-tag
              v-for="(tag, index) in scope.row.tags ? scope.row.tags.split(',') : []"
              :key="index"
              size="small"
              type="info"
              effect="plain"
              style="margin-right: 5px; margin-bottom: 5px;"
            >
              {{ tag }}
            </el-tag>
          </template>
        </el-table-column>
        
        <el-table-column label="状态" width="80" align="center">
          <template #default="scope">
            <el-tag :type="scope.row.status === 1 ? 'success' : 'danger'" size="small">
              {{ scope.row.status === 1 ? '上架' : '下架' }}
            </el-tag>
          </template>
        </el-table-column>
        
        <el-table-column label="操作" width="220" fixed="right" align="center">
          <template #default="scope">
            <el-button type="primary" size="small" icon="el-icon-edit" @click="handleEdit(scope.row)">编辑</el-button>
            <el-button type="danger" size="small" icon="el-icon-delete" @click="handleDelete(scope.row)">删除</el-button>
          </template>
        </el-table-column>
      </el-table>

      <!-- 分页 -->
      <div class="pagination-wrapper" v-if="total > 0">
        <el-pagination
          @size-change="handleSizeChange"
          @current-change="handleCurrentChange"
          :current-page="currentPage"
          :page-sizes="[10, 20, 30, 50]"
          :page-size="pageSize"
          layout="total, sizes, prev, pager, next, jumper"
          :total="total"
        ></el-pagination>
      </div>
    </div>

    <!-- 商品编辑弹窗 -->
    <el-dialog
      :title="dialogType === 'add' ? '新增商品' : '编辑商品'"
      v-model="dialogVisible"
      width="600px"
      @close="resetForm"
    >
      <el-form :model="form" :rules="rules" ref="form" label-width="100px">
        <el-form-item label="商品名称" prop="title">
          <el-input v-model="form.title" placeholder="请输入商品名称"></el-input>
        </el-form-item>
        
        <el-form-item label="店铺名称" prop="shopName">
          <el-input v-model="form.shopName" placeholder="请输入店铺名称"></el-input>
        </el-form-item>
        
        <el-form-item label="商品图片" prop="imageUrl">
          <el-input v-model="form.imageUrl" placeholder="请输入图片URL">
            <template slot="append">
              <el-button @click="previewImage">预览</el-button>
            </template>
          </el-input>
          <div class="image-preview" v-if="form.imageUrl">
            <img :src="form.imageUrl" alt="预览图" @click="previewImage">
          </div>
        </el-form-item>
        
        <el-row :gutter="20">
          <el-col :span="12">
            <el-form-item label="价格" prop="price">
              <el-input v-model="form.price" placeholder="售价">
                <template slot="prepend">¥</template>
              </el-input>
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="原价" prop="originalPrice">
              <el-input v-model="form.originalPrice" placeholder="原价">
                <template slot="prepend">¥</template>
              </el-input>
            </el-form-item>
          </el-col>
        </el-row>
        
        <el-row :gutter="20">
          <el-col :span="12">
            <el-form-item label="已售数量" prop="soldCount">
              <el-input-number v-model="form.soldCount" :min="0" style="width: 100%"></el-input-number>
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="库存" prop="stock">
              <el-input-number v-model="form.stock" :min="0" style="width: 100%"></el-input-number>
            </el-form-item>
          </el-col>
        </el-row>
        
        <el-form-item label="商品标签" prop="tags">
          <el-select
            v-model="selectedTags"
            multiple
            filterable
            allow-create
            default-first-option
            placeholder="请输入标签，回车创建"
            style="width: 100%"
          >
            <el-option
              v-for="item in tagOptions"
              :key="item"
              :label="item"
              :value="item">
            </el-option>
          </el-select>
          <div class="tag-tip">输入标签后按回车确认，多个标签用逗号分隔</div>
        </el-form-item>
        
        <el-form-item label="商品描述" prop="description">
          <el-input type="textarea" v-model="form.description" rows="3" placeholder="请输入商品描述"></el-input>
        </el-form-item>
        
        <el-form-item label="状态" prop="status">
          <el-radio-group v-model="form.status">
            <el-radio :label="1">上架</el-radio>
            <el-radio :label="0">下架</el-radio>
          </el-radio-group>
        </el-form-item>
      </el-form>
      
      <span slot="footer">
        <el-button @click="dialogVisible = false">取 消</el-button>
        <el-button type="primary" @click="submitForm" :loading="submitLoading">确 定</el-button>
      </span>
    </el-dialog>
    
    <!-- 图片预览弹窗 -->
    <el-dialog title="图片预览" v-model="previewVisible" width="400px">
      <img :src="form.imageUrl" style="width: 100%;" alt="预览图">
    </el-dialog>
  </div>
</template>

<script>
import axios from 'axios';

const BASE_URL = 'http://localhost:8090/user';

export default {
  name: 'ProductManagement',
  data() {
    return {
      tableData: [],
      loading: false,
      currentPage: 1,
      pageSize: 10,
      total: 0,
      searchKeyword: '',
      
      // 弹窗相关
      dialogVisible: false,
      dialogType: 'add', // add / edit
      submitLoading: false,
      previewVisible: false,
      
      // 表单数据
      form: {
        id: null,
        title: '',
        shopName: '',
        imageUrl: '',
        price: null,
        originalPrice: null,
        soldCount: 0,
        stock: 0,
        tags: '',
        description: '',
        status: 1
      },
      
      // 标签选择
      selectedTags: [],
      tagOptions: ['排队少', '运费险', '7天无理由退货', '立即体验', '万人复购', '限时特惠'],
      
      // 表单验证规则
      rules: {
        title: [
          { required: true, message: '请输入商品名称', trigger: 'blur' },
          { min: 2, max: 100, message: '长度在 2 到 100 个字符', trigger: 'blur' }
        ],
        shopName: [
          { required: true, message: '请输入店铺名称', trigger: 'blur' }
        ],
        price: [
          { required: true, message: '请输入价格', trigger: 'blur' },
          { pattern: /^\d+(\.\d{1,2})?$/, message: '请输入正确的价格格式', trigger: 'blur' }
        ],
        imageUrl: [
          { required: true, message: '请输入图片URL', trigger: 'blur' }
        ]
      }
    };
  },
  watch: {
    // 监听标签选择变化，同步到表单
    selectedTags(val) {
      this.form.tags = val.join(',');
    },
    
    // 编辑时，如果表单有tags，同步到selectedTags
    'form.tags': {
      handler(val) {
        if (val) {
          this.selectedTags = val.split(',');
        } else {
          this.selectedTags = [];
        }
      },
      immediate: true
    }
  },
  mounted() {
    this.init();
  },
  methods: {
    // 初始化加载数据
    init() {
      this.loading = true;
      const params = {
        page: this.currentPage,
        pageSize: this.pageSize,
        keyword: this.searchKeyword || undefined
      };
      
      // 这里使用getProductList接口，但需要分页参数
      axios.get(`${BASE_URL}/getProductList`, { params })
        .then(res => {
          this.loading = false;
          if (res.data && res.data.code === 200) {
            // 模拟分页（因为原接口不分页）
            let list = res.data.data || [];
            // 如果有搜索关键词，过滤
            if (this.searchKeyword) {
              list = list.filter(item => 
                item.title && item.title.includes(this.searchKeyword)
              );
            }
            this.total = list.length;
            // 手动分页
            const start = (this.currentPage - 1) * this.pageSize;
            const end = start + this.pageSize;
            this.tableData = list.slice(start, end);
          } else {
            this.$message.error(res.data.msg || '加载失败');
          }
        })
        .catch(err => {
          this.loading = false;
          console.error('加载商品失败', err);
          this.$message.error('网络异常，请稍后重试');
        });
    },
    
    // 搜索
    handleSearch() {
      this.currentPage = 1;
      this.init();
    },
    
    // 重置搜索
    resetSearch() {
      this.searchKeyword = '';
      this.handleSearch();
    },
    
    // 新增
    handleAdd() {
      this.dialogType = 'add';
      this.resetForm();
      this.dialogVisible = true;
    },
    
    // 编辑
    handleEdit(row) {
      this.dialogType = 'edit';
      // 深拷贝数据
      this.form = JSON.parse(JSON.stringify(row));
      this.dialogVisible = true;
    },
    
    // 删除
    handleDelete(row) {
      this.$confirm(`确定删除商品"${row.title}"吗？`, '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(() => {
        axios.delete(`${BASE_URL}/deleteProduct`, { params: { id: row.id } })
          .then(res => {
            if (res.data && res.data.code === 200) {
              this.$message.success('删除成功');
              this.init();
            } else {
              this.$message.error(res.data.msg || '删除失败');
            }
          })
          .catch(err => {
            console.error('删除失败', err);
            this.$message.error('网络异常');
          });
      }).catch(() => {});
    },
    
    // 提交表单
    submitForm() {
      this.$refs.form.validate(valid => {
        if (!valid) return;
        
        this.submitLoading = true;
        const url = this.dialogType === 'add' ? '/addProduct' : '/updateProduct';
        
        axios.post(`${BASE_URL}${url}`, this.form)
          .then(res => {
            this.submitLoading = false;
            if (res.data && res.data.code === 200) {
              this.$message.success(this.dialogType === 'add' ? '新增成功' : '修改成功');
              this.dialogVisible = false;
              this.init();
            } else {
              this.$message.error(res.data.msg || '操作失败');
            }
          })
          .catch(err => {
            this.submitLoading = false;
            console.error('提交失败', err);
            this.$message.error('网络异常');
          });
      });
    },
    
    // 重置表单
    resetForm() {
      this.form = {
        id: null,
        title: '',
        shopName: '',
        imageUrl: '',
        price: null,
        originalPrice: null,
        soldCount: 0,
        stock: 0,
        tags: '',
        description: '',
        status: 1
      };
      this.selectedTags = [];
      if (this.$refs.form) {
        this.$refs.form.clearValidate();
      }
    },
    
    // 预览图片
    previewImage() {
      if (this.form.imageUrl) {
        this.previewVisible = true;
      } else {
        this.$message.warning('请先输入图片URL');
      }
    },
    
    // 分页
    handleSizeChange(val) {
      this.pageSize = val;
      this.currentPage = 1;
      this.init();
    },
    
    handleCurrentChange(val) {
      this.currentPage = val;
      this.init();
    }
  }
};
</script>

<style lang="less" scoped>
.product-dashboard {
  padding: 20px;
  background-color: #f5f7fa;
  min-height: 100vh;
}

.header-section {
  text-align: center;
  margin-bottom: 20px;
  .page-title { font-size: 24px; color: #303133; margin-bottom: 5px; }
  .page-subtitle { color: #909399; font-size: 14px; }
}

.container {
  max-width: 1400px;
  margin: 0 auto;
  background: #fff;
  padding: 20px;
  border-radius: 8px;
  box-shadow: 0 2px 12px 0 rgba(0,0,0,0.05);
}

.tableBar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 20px;
  flex-wrap: wrap;
  gap: 15px;
}

.data-table {
  margin-bottom: 20px;
}

.price-text {
  color: #F56C6C;
  font-weight: bold;
  font-size: 16px;
}

.original-price {
  font-size: 12px;
  color: #909399;
  text-decoration: line-through;
  margin-left: 5px;
  display: block;
}

.image-error {
  width: 60px;
  height: 60px;
  background: #f5f5f5;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #909399;
  font-size: 20px;
}

.image-preview {
  margin-top: 10px;
  img {
    width: 100px;
    height: 100px;
    object-fit: cover;
    border-radius: 4px;
    cursor: pointer;
    border: 1px solid #EBEEF5;
    &:hover { opacity: 0.8; }
  }
}

.tag-tip {
  font-size: 12px;
  color: #909399;
  margin-top: 5px;
}

.pagination-wrapper {
  margin-top: 20px;
  display: flex;
  justify-content: center;
}
</style>
